import { logger } from '@/utils/logger';
import axios from 'axios';

export const API_BASE_URL =
  import.meta.env.MODE === 'development'
    ? 'http://localhost:3003'
    : 'https://platform-api.us.catalyzed.ai';

export const apiClient = axios.create({
  ...axios.defaults,
  baseURL: API_BASE_URL,
  timeout: 10_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => {
    // Convert arrays to comma-separated strings
    return Object.entries(params)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const filteredValues = value
            .filter((v) => v !== undefined)
            .map(encodeURIComponent);
          return `${key}=${filteredValues.join(',')}`;
        } else if (value instanceof Date) {
          return `${key}=${value.toISOString()}`;
        } else if (value === undefined) {
          return '';
        }
        return `${key}=${value}`;
      })
      .join('&');
  },
});

apiClient.interceptors.request.use(
  function (config) {
    // Ensure credentials are always included
    config.withCredentials = true;

    // Log API requests in development
    logger.debug('API Request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params,
    });

    return config;
  },
  function (error) {
    logger.error('API Request Error', { error });
    return Promise.reject(error);
  },
);

// Add response interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => {
    // Log API responses in development
    logger.debug('API Response', {
      status: response.status,
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
    });
    return response;
  },
  (error) => {
    // Log API errors
    logger.error('API Response Error', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      message: error.message,
      data: error.response?.data,
    });
    return Promise.reject(error);
  },
);

export type QueryOptions<T = unknown> = {
  enabled?: boolean;
  retry?: boolean;
  keepPreviousData?: boolean;
  refetchInterval?: number | ((data: T) => number | false);
};
